import type { Bucket } from '@outpost/api-client';

import { cn } from '@/lib/utils';

/**
 * Event volume over time for one issue.
 *
 * Deliberately unlabelled and tiny: in a dense list its job is to show *shape*
 * — is this spiking, steady, or tailing off — not exact values. The detail page
 * carries the readable chart.
 */
export function Sparkline({
  buckets,
  className,
  width = 120,
  height = 28,
}: {
  buckets: Bucket[];
  className?: string;
  width?: number;
  height?: number;
}) {
  // An empty svg rather than a div, so a class like `w-full` sizes the
  // placeholder exactly as it will the chart and nothing shifts on load.
  if (buckets.length === 0) {
    return <svg width={width} height={height} className={className} aria-hidden />;
  }

  const counts = buckets.map((bucket) => bucket.count);
  const max = Math.max(...counts, 1);
  const step = buckets.length > 1 ? width / (buckets.length - 1) : width;

  const points = counts.map((count, index) => {
    const x = index * step;
    // Leave a pixel of headroom so a full-height bar is not clipped.
    const y = height - 1 - (count / max) * (height - 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const total = counts.reduce((sum, count) => sum + count, 0);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      // Stretch rather than letterbox when a class makes the chart fluid, and
      // keep the line the same weight at any width.
      preserveAspectRatio="none"
      className={cn('overflow-visible', className)}
      role="img"
      aria-label={`${total} events over the last ${buckets.length} hours`}
    >
      <polyline
        points={`0,${height} ${points.join(' ')} ${width},${height}`}
        className="fill-chart-fill stroke-none"
      />
      <polyline
        points={points.join(' ')}
        className="fill-none stroke-chart-line"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
